// =============================================================================
// useCanOpenModule — would the route gate let this person open `moduleKey`?
//
// App.tsx's RequireModule answers a denial with <Navigate to="/" replace/>, so
// a cross-link to a module the viewer lacks lands them on Home with no message.
// Ask the SAME predicate the route gate and the nav use (hasModule) BEFORE the
// link is offered, and offer plain text instead when the answer is no.
//
// null while the three reads it needs are still deciding — the route gate's
// own isPending rule (finding H4) — so a caller renders nothing yet rather
// than flashing a link at a viewer who is about to be denied.
//
// The general form of useCanFinancial (InvoiceHistoryCard) and useCanPlanSpray
// (sprayPlanLink): prefer this for a new cross-link.
// =============================================================================

import { hasAnyModule, hasModule, MODULES } from '@/lib/modules'
import { useMoneyLock } from '@/lib/moneyLock'
import { useCurrentUser } from '@/lib/useCurrentUser'
import { useActiveTenant } from '@/lib/useActiveTenant'
import { usePermissions } from '@/lib/usePermissions'

export function useCanOpenModule(moduleKey: string): boolean | null {
  return useModuleGate()(moduleKey)
}

/** useCanOpenModule for a page that several modules open (the route gate's
 *  any-of form, hasAnyModule): true when the viewer holds ANY key, null while
 *  the reads decide. */
export function useCanOpenAnyModule(moduleKeys: readonly string[]): boolean | null {
  const { data: me, isPending: mePending } = useCurrentUser()
  const { data: tenant, isPending: tenantPending } = useActiveTenant()
  const { perms, isPending: permsPending } = usePermissions()
  if (mePending || tenantPending || permsPending) return null
  return hasAnyModule(me ?? null, moduleKeys, tenant?.enabledModules ?? null, perms, tenant?.slug)
}

/** The same question for a screen that offers SEVERAL cross-links (the Farm
 *  Map's block panel has a row kind and a button per module): one set of
 *  reads, one answer per key. Each answer is null while the reads decide. */
export function useModuleGate(): (moduleKey: string) => boolean | null {
  const { data: me, isPending: mePending } = useCurrentUser()
  const { data: tenant, isPending: tenantPending } = useActiveTenant()
  const { perms, isPending: permsPending } = usePermissions()
  const pending = mePending || tenantPending || permsPending
  return (moduleKey: string) => pending
    ? null
    : hasModule(me ?? null, moduleKey, tenant?.enabledModules ?? null, perms, tenant?.slug)
}

/** useCanOpenModule plus the money lock — the route's SECOND gate. A
 *  `money: 'core'` module (Transport Recon, Financial…) behind a locked tenant
 *  renders MoneyLockedPanel instead of the page, so a link to it is not one this
 *  person can use right now: offer the sentence, not the link. Same null-while-
 *  deciding rule as useCanOpenModule. */
export function useCanUseModule(moduleKey: string): boolean | null {
  const open = useCanOpenModule(moduleKey)
  const { locked } = useMoneyLock()
  if (open !== true) return open
  return !(locked && MODULES.find(m => m.key === moduleKey)?.money === 'core')
}
